import constants from '../shared/constants';
import { getCookie, isSupportCookieStore } from '../shared/functions';
import { HeroPlaybackState, HeroVideoController, YouTubeMessage } from '../shared/types';

document.addEventListener('DOMContentLoaded', async (): Promise<void> => {
    const heroVideos: NodeListOf<HTMLElement> = document.querySelectorAll<HTMLElement>(constants.targets.heroVideo.root);
    if (!heroVideos.length) return;

    const heroVideoConstants = constants.targets.heroVideo;
    const heroVideoDefaults = heroVideoConstants.defaults;
    const heroVideoStates = heroVideoConstants.states;
    const heroVideoDomains = heroVideoConstants.domains;

    let hasConsent: boolean = (await getCookie(constants.cookies.consent)) === constants.cookies.consentAccepted;
    const heroVideoControllers = new Map<string, HeroVideoController>();

    const isYouTubeOrigin = (origin: string): boolean =>
        origin.includes(heroVideoDomains.youtube) || origin.includes(heroVideoDomains.youtubeNoCookie);

    const postPlayerMessage = ($iframe: HTMLIFrameElement, payload: Record<string, unknown>): void => {
        $iframe.contentWindow?.postMessage(JSON.stringify(payload), '*');
    };

    const postPlayerCommand = ($iframe: HTMLIFrameElement, command: 'playVideo' | 'pauseVideo' | 'seekTo', args: unknown[] = []): void => {
        postPlayerMessage($iframe, {
            event: 'command',
            func: command,
            args,
        });
    };

    const registerYouTubeListener = ($iframe: HTMLIFrameElement): void => {
        if (!$iframe.id || !$iframe.getAttribute('src')) return;

        window.setTimeout(() => {
            postPlayerMessage($iframe, {
                event: 'listening',
                id: $iframe.id,
                channel: 'hero-video',
            });
        }, 150);
    };

    const syncToggleState = ($toggle: HTMLElement | null, playbackState: HeroPlaybackState): void => {
        if (!$toggle) return;

        const $playIcon = $toggle.querySelector<HTMLElement>(heroVideoConstants.playIcon);
        const $pauseIcon = $toggle.querySelector<HTMLElement>(heroVideoConstants.pauseIcon);
        const $replayIcon = $toggle.querySelector<HTMLElement>(heroVideoConstants.replayIcon);
        const playLabel = $toggle.getAttribute('data-play-label') ?? heroVideoDefaults.playLabel;
        const pauseLabel = $toggle.getAttribute('data-pause-label') ?? heroVideoDefaults.pauseLabel;
        const replayLabel = $toggle.getAttribute('data-replay-label') ?? heroVideoDefaults.replayLabel;

        $toggle.setAttribute('data-playback-state', playbackState);
        $toggle.setAttribute(
            'aria-label',
            playbackState === heroVideoStates.playing
                ? pauseLabel
                : playbackState === heroVideoStates.ended
                    ? replayLabel
                    : playLabel
        );

        $playIcon?.classList.toggle(constants.classes.hidden, playbackState !== heroVideoStates.paused);
        $pauseIcon?.classList.toggle(constants.classes.hidden, playbackState !== heroVideoStates.playing);
        $replayIcon?.classList.toggle(constants.classes.hidden, playbackState !== heroVideoStates.ended);
    };

    const syncHeroVideo = ($heroVideo: HTMLElement): void => {
        const $embed = $heroVideo.querySelector<HTMLElement>(heroVideoConstants.embed);
        const $section = $heroVideo.closest('section');
        const $warning = $section?.querySelector<HTMLElement>(heroVideoConstants.warning) ?? null;
        const $toggle = $heroVideo.querySelector<HTMLElement>(heroVideoConstants.toggle) ?? null;
        const $iframe = $embed?.querySelector<HTMLIFrameElement>('iframe') ?? null;
        const videoSrc = $heroVideo.dataset.videoSrc;

        if (!$embed || !$iframe || !videoSrc) return;

        if (!$iframe.id) {
            $iframe.id = `hero-video-${heroVideoControllers.size + 1}`;
        }

        heroVideoControllers.set($iframe.id, {
            $iframe,
            $toggle,
        });

        if ($iframe.dataset.heroVideoBound !== heroVideoDefaults.boundValue) {
            $iframe.addEventListener('load', () => registerYouTubeListener($iframe));
            $iframe.dataset.heroVideoBound = heroVideoDefaults.boundValue;
        }

        if (hasConsent) {
            if (!$iframe.getAttribute('src')) {
                $iframe.src = videoSrc;
            } else {
                registerYouTubeListener($iframe);
            }

            $embed.classList.remove(constants.classes.hidden);
            $toggle?.classList.remove(constants.classes.hidden);
            $warning?.classList.add(constants.classes.hidden);

            if (($toggle?.getAttribute('data-playback-state') ?? '') !== heroVideoStates.ended) {
                syncToggleState($toggle, heroVideoStates.playing);
            }
            return;
        }

        $iframe.src = 'about:blank';
        $iframe.removeAttribute('src');
        $embed.classList.add(constants.classes.hidden);
        $toggle?.classList.add(constants.classes.hidden);
        $warning?.classList.remove(constants.classes.hidden);
        syncToggleState($toggle, heroVideoStates.paused);
    };

    const syncPlayerState = ($iframe: HTMLIFrameElement, playerState: number): void => {
        const controller = heroVideoControllers.get($iframe.id);
        if (!controller) return;

        if (playerState === 1) {
            syncToggleState(controller.$toggle, heroVideoStates.playing);
            return;
        }

        if (playerState === 2) {
            syncToggleState(controller.$toggle, heroVideoStates.paused);
            return;
        }

        if (playerState === 0) {
            syncToggleState(controller.$toggle, heroVideoStates.ended);
        }
    };

    window.addEventListener('message', (event: MessageEvent) => {
        if (!isYouTubeOrigin(event.origin)) return;

        let data: YouTubeMessage | null = null;
        if (typeof event.data === 'string') {
            try {
                data = JSON.parse(event.data) as YouTubeMessage;
            } catch {
                return;
            }
        } else if (typeof event.data === 'object' && event.data !== null) {
            data = event.data as YouTubeMessage;
        }

        if (!data?.id) return;

        const controller = heroVideoControllers.get(data.id);
        if (!controller) return;

        if (data.event === 'onStateChange' && typeof data.info === 'number') {
            syncPlayerState(controller.$iframe, data.info);
            return;
        }

        if (data.event === 'infoDelivery' && typeof data.info === 'object' && typeof data.info?.playerState === 'number') {
            syncPlayerState(controller.$iframe, data.info.playerState);
        }
    });

    heroVideos.forEach(($heroVideo: HTMLElement) => {
        const $toggle = $heroVideo.querySelector<HTMLElement>(heroVideoConstants.toggle);
        const $embed = $heroVideo.querySelector<HTMLElement>(heroVideoConstants.embed);
        const $iframe = $embed?.querySelector<HTMLIFrameElement>('iframe') ?? null;

        syncHeroVideo($heroVideo);

        if (!$toggle || !$iframe) return;

        $toggle.addEventListener('click', () => {
            if (!hasConsent || !$iframe.getAttribute('src')) return;

            const playbackState = ($toggle.getAttribute('data-playback-state') ?? heroVideoStates.paused) as HeroPlaybackState;

            if (playbackState === heroVideoStates.playing) {
                postPlayerCommand($iframe, 'pauseVideo');
                syncToggleState($toggle, heroVideoStates.paused);
                return;
            }

            if (playbackState === heroVideoStates.ended) {
                postPlayerCommand($iframe, 'seekTo', [0, true]);
            }

            postPlayerCommand($iframe, 'playVideo');
            syncToggleState($toggle, heroVideoStates.playing);
        });
    });

    if (isSupportCookieStore) {
        (window as any).cookieStore.addEventListener('change', async ({ changed, deleted }: { changed: Array<{ name: string; value: string }>, deleted: Array<{ name: string }> }) => {
            const occChanged = changed.find(c => c.name === constants.cookies.consent);
            const occDeleted = deleted.find(c => c.name === constants.cookies.consent);

            if (!occChanged && !occDeleted) return;

            hasConsent = occChanged?.value === constants.cookies.consentAccepted;
            heroVideos.forEach(($heroVideo: HTMLElement) => syncHeroVideo($heroVideo));
        });
    }
});
